<template>
  <div :class="[isMobile ? 'ans-facet-full' : 'ans-facet-sidebar', 'terminology-facet-container']">
    <!-- Facet Terminologies Bloc -->
    <div class="panel panel-default facet-bloc">
      <div class="panel-heading">
        <h6 class="panel-title">
          <strong>Terminologies</strong>
        </h6>
      </div>
      <div class="panel-body">
        <!-- Global results counter -->
        <div v-if="initialNumberOfConcepts > 0" class="text-center">
          <small class="text-muted">{{ initialNumberOfConcepts }} concepts trouvés</small>
        </div>
        <div>
          <button class="check-uncheck-btn btn btn-link btn-sm" type="button" @click="checkOrUnCheckAllTerminologies()">
            {{ checkOrUnCheckAllTerminologiesText }}
          </button>
        </div>

        <TerminologyFacetTree :items="store.facets.options.all" :parentPrefix="'parent-'" :childPrefix="'child-'"
          :singlePrefix="'single-'" :itemKey="item => item.UUID" @toggle-selection="toggleSelection" />
      </div>
    </div>

  </div>
</template>

<script>

import ConceptsStore from "../concepts-store";
import TerminologyFacetTree from "./TerminologyFacetTree";

export default {
  name: "TerminologyFacet",
  components: { TerminologyFacetTree },

  data() {
    return {
      store: ConceptsStore.state,
      selectedOptions: [],
      initialNumberOfConcepts: 0,  // Stocke le nombre initial de concepts
      cbSelectAll: true,
    }
  },
  //test
  created() {
    this.selectedOptions = this.getAllOptions();
    // Stocker le nombre initial de concepts pour l'affichage fixe
    this.initialNumberOfConcepts = this.store.numberOfConcepts;
  },

  computed: {
    /**
     * Texte du bouton pour sélectionner/désélectionner toutes les terminologies
     * @returns {string} Le texte du bouton
     */
    checkOrUnCheckAllTerminologiesText() {
      return this.cbSelectAll ? "Tout désélectionner" : "Tout sélectionner"
    },

    /**
     * Vérifie si toutes les terminologies sont actuellement sélectionnées
     * @returns {boolean} True si toutes les terminologies sont sélectionnées, false sinon
     */
    uncheckAllTerminologies() {
      const allOptions = this.getAllOptions();
      return allOptions.length > 0 && allOptions.length === this.selectedOptions.length;
    },

    /**
     * Vérifie si l'affichage est sur un appareil mobile
     * @returns {boolean} True si l'écran est inférieur à 768px, false sinon
     */
    isMobile() {
      return window.innerWidth < 768;
    }
  },

  methods: {
    /**
     * Met à jour l'URL pour inclure tous les UUID des terminologies sélectionnées (itemType = terminology)
     * Utilise l'attribut 'checked' du parent et des enfants pour déterminer l'état de sélection
     * Si le parent.checked === 'yes', ajoute l'ID du parent
     * Sinon, ajoute les IDs des enfants où child.checked === 'yes'
     */
    updateSelectedTerminologiesInUrl() {
      const selectedTerminologyIds = [];

      this.store.facets.options.all.forEach(terminology => {
        if (terminology.checked) {
          selectedTerminologyIds.push(terminology.id);
        }
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('terminology');
      selectedTerminologyIds.forEach(id => {
        url.searchParams.append('terminology', id);
      });
      window.history.replaceState({}, '', url.toString());
    },

    /**
     * Récupère toutes les options de terminologies disponibles
     * Pour les terminologies avec enfants, retourne uniquement les enfants
     * Pour les terminologies sans enfants, retourne la terminologie elle-même
     * @returns {Array} Tableau de toutes les options de terminologies
     */
    getAllOptions() {
      let allOptions = [];
      this.store.facets.options.all.forEach((terminology) => {
        allOptions.push(terminology);
      });
      return allOptions;
    },

    /**
     * Formate les options sélectionnées pour l'API
     * Si aucune option n'est sélectionnée, retourne une facette impossible pour ne retourner aucun résultat
     * @param {Array} options - Les options sélectionnées à formater
     * @returns {Array} Tableau d'objets formatés pour l'API avec terminoName et dcTypeFacets
     */
    formatOptionsForAPI(options) {
      // Si aucune option n'est sélectionnée, retourner une facette impossible
      // pour forcer l'API à ne retourner aucun résultat
      if (options.length === 0) {
        return [{
          "terminoName": "__NONE_SELECTED__",
          "dcTypeFacets": []
        }];
      }

      let terminologyOptions = [];
      let dcTypeOptions = {};

      options.forEach((option) => {

        if (option.parent) {
          if (!(option.parent in dcTypeOptions)) dcTypeOptions[option.parent] = [];
          dcTypeOptions[option.parent].push({ "dcType": option.id });
        }

        let terminologyOption = {
          "terminoName": (option.parent) ? option.parent : option.id,
          "dcTypeFacets": []
        };

        if (!terminologyOptions.some((el) => el.terminoName === terminologyOption.terminoName)) terminologyOptions.push(terminologyOption);
      });

      return terminologyOptions.map((terminology) => {
        terminology.dcTypeFacets = dcTypeOptions[terminology.terminoName] || [];
        return terminology;
      });
    },

    /**
     * Met à jour la liste des concepts en tenant compte des terminologies filtrées via la facette
     * Réinitialise la page à 1 et effectue une nouvelle recherche sans réinitialiser les facettes
     * Met à jour les paramètres URL avec les terminologies sélectionnées
     */
    refineSearch() {
      this.store.facets.options.selected = this.formatOptionsForAPI(this.selectedOptions) || [];
      // Generate array of terminology IDs from this.store.facets.options.all where checked == false
      const terminologyIds = this.store.facets.options.all
        .filter(option => option.checked === true)
        .map(option => option.id);

      ConceptsStore.setPage(1);
      // Effectuer la recherche en fonction du type de recherche actuel
      // Utilisation de true pour ne pas réinitialiser les facettes lors du filtrage
      if (terminologyIds.length === 0) {
        ConceptsStore.resetFacetsList();
      } else {
        ConceptsStore.searchConcepts(true, terminologyIds);
      }
      // Mettre à jour l'URL avec les terminologies sélectionnées après la recherche
      this.updateSelectedTerminologiesInUrl();
    },

    /**
     * Sélectionne ou désélectionne toutes les terminologies
     * Bascule entre sélectionner toutes les options ou tout désélectionner
     * Met à jour le store et déclenche une nouvelle recherche
     */
    checkOrUnCheckAllTerminologies() {
      this.cbSelectAll = !this.cbSelectAll;
      this.selectedOptions = this.cbSelectAll ? this.getAllOptions() : [];

      this.store.facets.options.all.forEach((option) => {
        option.checked = this.cbSelectAll && !option.protected;
      });

      // Relancer la recherche lorsque toutes les terminologies sont sélectionnées
      if (this.cbSelectAll) {
        this.refineSearch();
      } else {
        ConceptsStore.resetFacetsList();
      }
    },


    /**
     * Bascule la sélection d'un élément enfant individuel
     * Met à jour l'attribut 'checked' entre 'yes' et 'no'
     * @param {Object} item - L'élément à basculer
     */
    toggleSelection(item) {
      this.$emit('toggle-selection', item);
      const index = this.selectedOptions.findIndex(selected => selected.UUID === item.UUID);
      if (index > -1) {
        this.selectedOptions.splice(index, 1);
      } else {
        this.selectedOptions.push(item);
      }
      item.checked = !item.checked;
      this.updateSelectedTerminologiesInUrl();
    }
  }
}
</script>

<style lang="scss" scoped>
.terminology-facet-container {
  margin-bottom: 20px;
}

.ans-facet-full {
  position: fixed;
  width: 95%;
  z-index: 9999;
  left: 50%;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
}

.facet-bloc {
  margin-bottom: 20px;
  background-color: #FFFFFF;
  border: 1px solid #e0e0e0;
  border-radius: 4px;

  &:last-child {
    margin-bottom: 0;
  }

  .panel-heading {
    background-color: #1D71B8;
    border-bottom: 1px solid #dee2e6;
    text-align: center;
    padding: 15px 10px;

    .panel-title {
      color: #FFFFFF;
      margin: 0;
      font-size: 18px;
    }
  }

  .panel-body {
    max-height: 30rem;
    overflow: auto;
    padding: 10px;
    background-color: #FFFFFF;
  }
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .terminology-facet-container {
    width: 100%;
    position: static;
  }

  .ans-facet-full {
    width: 100%;
    position: static;
    transform: none;
    left: auto;
    top: auto;
    z-index: auto;
    overflow-y: visible;
  }

  .facet-bloc {
    margin-bottom: 15px;
  }

  .panel-body {
    max-height: none;
    overflow-y: visible;
  }
}

@media (min-width: 768px) and (max-width: 991.98px) {
  .ans-facet-sidebar {
    position: static;
    margin-bottom: 1rem;
  }

  .panel-body {
    max-height: none;
    overflow-y: visible;
  }
}
</style>